import { type CountryCode } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiGetFpPayment } from '@hooks-api/fortune/useApiGetFpPayment'
import { ECommerceOrderType } from '@hooks-api/types/dataModel/eCommercePayments'
import { type BaseCheckoutPayFlowModule } from '@payments/lib/checkout-pay/BaseCheckoutPayFlowModule'
import { useRef } from 'react'
import { checkoutPayFlowController } from '../core/checkoutPayFlowController'
import * as paymentService from '../core/paymentService'
import { type AlipayDialogContext } from './use-alipay-dialog-flow-actions'
import { CKO_REDIRECT_APM_OPTION_ID_KEY } from './use-cko-redirect-apm-execution'

export interface UseFpAlipayDialogFlowActionsParams {
  paymentModule: BaseCheckoutPayFlowModule
  countryCode: CountryCode
}

export const useFpAlipayDialogFlowActions = ({ paymentModule, countryCode }: UseFpAlipayDialogFlowActionsParams) => {
  const { trigger: getPayment } = useApiGetFpPayment()

  const activeCkoMountRef = useRef<paymentService.CkoMount | null>(null)

  const mountAlipayComponent = useCallbackEvent(async (context: AlipayDialogContext, containerId: string) => {
    const initResult = await getPayment({
      order_type: ECommerceOrderType.TRANSACTION,
      order_id: context.paymentTransactionId,
      payment_method_id: context.paymentOptionId,
      remember_payment_card_token: false,
      installment: null,
      payment_success_url: context.paymentSuccessUrl,
      payment_fallback_url: context.paymentFallbackUrl,
      email: context.email,
    })
    const paymentInfo = initResult?.data?.data
    if (!paymentInfo) {
      throw new Error('[useFpAlipayDialogFlowActions] getPayment returned empty paymentInfo')
    }
    activeCkoMountRef.current = await paymentService.applyCkoSession(paymentModule, paymentInfo, paymentModule.getAppEnv(), containerId, { countryCode })
    sessionStorage.setItem(CKO_REDIRECT_APM_OPTION_ID_KEY, String(context.paymentOptionId))
  })

  // 成功時整頁 redirect（此 promise 不會 resolve，無妨）；失敗時 Flow onError 經 controller reject → dialog 顯示錯誤。
  const awaitFlowResult = useCallbackEvent(async (context: AlipayDialogContext) => {
    await checkoutPayFlowController.awaitPaymentId({
      paymentTransactionId: context.paymentTransactionId,
      orderHash: context.orderHash,
    })
  })

  const cancelInFlight = useCallbackEvent((reason: string) => {
    checkoutPayFlowController.cancelInFlight(reason)
    activeCkoMountRef.current?.unmount()
    activeCkoMountRef.current = null
  })

  return {
    mountAlipayComponent,
    awaitFlowResult,
    cancelInFlight,
  }
}
